// PWA home-screen unread badge — kept in sync with /api/me/unread-count.
//
// The Badging API:
//   navigator.setAppBadge(count)   → red number bubble on the icon
//   navigator.clearAppBadge()      → no badge
//
// Supported in iOS 16.4+ standalone PWAs, Android Chromium, desktop Edge/
// Chrome. Silently no-op everywhere else.

// Standalone (not `extends Navigator`) — newer lib.dom types these methods,
// and re-declaring them as optional conflicts with the built-in signatures.
interface BadgingNavigator {
  setAppBadge?: (count?: number) => Promise<void>;
  clearAppBadge?: () => Promise<void>;
}

function nav(): BadgingNavigator | null {
  return typeof navigator === 'undefined' ? null : (navigator as unknown as BadgingNavigator);
}

async function applyBadge(count: number): Promise<void> {
  const n = nav();
  if (!n?.setAppBadge) return;
  try {
    if (count > 0) await n.setAppBadge(count);
    else await n.clearAppBadge?.();
  } catch {
    // Some browsers reject if not installed; safe to swallow.
  }
}

let pollTimer: ReturnType<typeof setInterval> | null = null;
let lastCount = -1;

async function refresh(): Promise<void> {
  try {
    const r = await fetch('/api/me/unread-count', { credentials: 'include' });
    if (!r.ok) return; // logged out — leave the badge alone
    const { count } = (await r.json()) as { count: number };
    if (count !== lastCount) {
      lastCount = count;
      await applyBadge(count);
    }
  } catch {
    /* network blip — try again next tick */
  }
}

/** Start polling the unread count every 60s and mirror it onto the PWA
 *  badge. Re-runs immediately on tab focus. Safe to call multiple times —
 *  later calls are no-ops if a poll is already running. */
export function startUnreadBadgeSync(): void {
  if (typeof window === 'undefined') return;
  const n = nav();
  if (!n?.setAppBadge) return; // unsupported browser → don't waste polls
  if (pollTimer) return;

  void refresh();
  pollTimer = setInterval(refresh, 60_000);
  window.addEventListener('focus', refresh);
  // Also nudge when a thread mutation invalidates the threads query — that's
  // the moment unread state most likely changed.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void refresh();
  });
}

/** Force an immediate refresh from a mutation callback. */
export function bumpUnreadBadge(): void {
  void refresh();
}
